import { Raycaster, Vector3 } from "three";
import { Camera } from "../scenegraph/internal";
import { Behavior } from "./internal";
/**
 * Handles the zoom and pan behavior of quadtree viewer
 *
 * @class ZoomPanBehaviour
 * @param {SceneGraphNode} parentObject
 * @extends Behavior
 * @constructor
 */
export class ZoomPanBehaviour extends Behavior {
    /**
     * @method constructor
     * @param {SceneGraphNode} parentObject
     */
    constructor(parentObject) {
        super(parentObject);
        this.minZoom = 0.25;
        this.maxZoom = 10;
        this._panX = 0;
        this._panY = 0;
        this._zoom = 1.0;
        this._curX = 0;
        this._curY = 0;
        this._zoomDelta = 0;
        this._tMouseVector = null;
        if (!(parentObject instanceof Camera)) {
            throw "Wrong Parent Object: ZoomPanBehavior Behavior must be attached to a Camera object.";
        }
        this._tMouseVector = new Vector3();
    }
    /**
     * Will be called in every render pass
     *
     * reads:   traversalstate.mousewheel
     *          traversalstate.mousewheelarguments
     *          traversalstate.pan
     *          traversalstate.panarguments
     *
     * writes:  traversaltstate.panX
     *          traversalstate.panY
     *          traversalstate.zoom
     *
     * @method render
     * @param {TraversalState} traversalState
     */
    render(traversalState) {
        if (this.disable || traversalState.lockNavigation)
            return;
        let mx;
        let my;
        if (traversalState.mouseWheel) {
            mx = traversalState.mouseWheelArguments.offsetX;
            my = traversalState.mouseWheelArguments.offsetY;
            const mapcoord = [(mx - this._panX) / this._zoom, (my - this._panY) / this._zoom];
            this._zoom = this._zoom + -traversalState.mouseWheelArguments.deltaY * 0.1;
            if (this._zoom < this.minZoom)
                this._zoom = this.minZoom;
            if (this._zoom > this.maxZoom)
                this._zoom = this.maxZoom;
            // update zoom level of quadtree:
            this.curZoom = Math.min(Math.max(Math.floor(this._zoom), 1), this.maxZoom);
            const deltamouse = [mapcoord[0] * this._zoom + this._panX, mapcoord[1] * this._zoom + this._panY];
            const dx = mx - deltamouse[0];
            const dy = my - deltamouse[1];
            this._panX += dx;
            this._panY += dy;
        }
        if (traversalState.mouseDown) {
            this._curX = traversalState.mouseDownArguments.offsetX;
            this._curY = traversalState.mouseDownArguments.offsetY;
        }
        if (traversalState.pan) {
            mx = traversalState.panArguments["offsetX"];
            my = traversalState.panArguments["offsetY"];
            this._panX += mx - this._curX;
            this._panY += my - this._curY;
            this._curX = mx;
            this._curY = my;
        }
        traversalState.panX = this._panX;
        traversalState.panY = this._panY;
        traversalState.zoom = this._zoom;
        traversalState.sceneZoom =
            (Math.sqrt(this._zoom) - Math.sqrt(this.minZoom)) / (Math.sqrt(this.maxZoom) - Math.sqrt(this.minZoom));
        //Resizes the camera in case the context has been resized.
        this.parentObject.iCamera.left = traversalState.scene.engine.getContextWidth() / -2;
        this.parentObject.iCamera.right = traversalState.scene.engine.getContextWidth() / 2;
        this.parentObject.iCamera.top = traversalState.scene.engine.getContextHeight() / 2;
        this.parentObject.iCamera.bottom = traversalState.scene.engine.getContextHeight() / -2;
        this.parentObject.iCamera.updateProjectionMatrix();
        //update the raycaster for picking
        this._tMouseVector.x =
            2 * (traversalState.actualMousePos[0] / traversalState.scene.engine.getContextWidth()) - 1;
        this._tMouseVector.y =
            1 - 2 * (traversalState.actualMousePos[1] / traversalState.scene.engine.getContextHeight());
        // set two vectors with opposing z values
        this._tMouseVector.z = -1.0;
        let vector = this._tMouseVector.clone();
        const camera = this.parentObject.iCamera;
        let end = new Vector3(vector.x, vector.y, 1.0);
        vector = vector.unproject(camera);
        end = end.unproject(camera);
        // find direction from vector to end
        end.sub(vector).normalize();
        traversalState.rayCasterQuadtreeScene = new Raycaster(vector, end); //this._tProjector.pickingRay( this._tMouseVector.clone(), traversalState.cameras["sceneCamera"].iCamera );
        traversalState.rayCasterQuadtreeScene.params.Points.threshold = 0.1; //distance in welcher Punkte selektiert werden -> 5cm
    }
    reset() {
        this._panX = 0;
        this._panY = 0;
        this._zoom = 1.0;
    }
    setZoomPan(zoom, panX, panY) {
        this._zoom = zoom > this.minZoom ? (zoom < this.maxZoom ? zoom : this.maxZoom) : this.minZoom;
        this._panX = panX;
        this._panY = panY;
    }
    getZoomPan() {
        return [this._zoom, this._panX, this._panY];
    }
}
//# sourceMappingURL=zoompanbehavior.js.map